// client/src/coms/voip/p2pScreenShare.js
import { RoomEvent } from 'livekit-client';
import { publishExistingScreenTrackToLiveKit, unpublishScreenTrackFromLiveKit } from './voiceClient';
import { captureScreenShareMedia } from './screenShareAudioManager';
import { voiceState } from './voiceclient_parts/state';
import { participantMayShare, remoteMayShare } from './sharePermissions';

const stateByRoom = new WeakMap();
let lastBoundRoom = null;

function getState(room) {
  const r = room || null;
  if (!r) return null;

  let s = stateByRoom.get(r);
  if (!s) {
    s = {
      enabled: false,
      sending: false,
      receiveEnabled: false,
      localStream: null,
      localTrack: null,
      localAudioTrack: null,
      maxPeers: 1,
      peers: new Map(),
      remoteStreamsByIdentity: new Map(),
      err: '',
      bound: false,
      quality: '720',
      fps: 20,
      transport: 'p2p',
      livekitPub: null,
      fallback: {
        enabled: true,
        timeoutMs: 6500,
      },
      fallbackTimer: null,
      fallbackDebug: {
        lastCheckAt: 0,
        lastReason: '',
        lastTriggered: false,
        lastPeerCount: 0,
        lastConnectedPeerCount: 0,
        lastWantedPeers: 0,
      },
      ice: {
        mode: 'auto',
        cachedServers: null,
        cachedAt: 0,
        cacheMs: 60 * 1000,
      },
    };
    stateByRoom.set(r, s);
  }
  return s;
}

function stopTracks(stream) {
  const s = stream || null;
  if (!s) return;

  try {
    const tracks = Array.from(s.getTracks ? s.getTracks() : []);
    for (const t of tracks) {
      try {
        t.stop();
      } catch {}
    }
  } catch {}
}

function encode(obj) {
  return new TextEncoder().encode(JSON.stringify(obj));
}

function decode(payload) {
  try {
    const txt = new TextDecoder().decode(payload);
    return JSON.parse(txt);
  } catch {
    return null;
  }
}

function clearFallbackTimer(state) {
  try {
    if (state && state.fallbackTimer) clearTimeout(state.fallbackTimer);
  } catch {}
  if (state) state.fallbackTimer = null;
}

function countSenderPeers(state) {
  const s = state || null;
  if (!s) return 0;
  let count = 0;
  try {
    for (const p of Array.from(s.peers.values())) {
      if (!p || p.role !== 'sender' || !p.pc) continue;
      count += 1;
    }
  } catch {}
  return count;
}

function countConnectedSenderPeers(state) {
  const s = state || null;
  if (!s) return 0;
  let count = 0;
  try {
    for (const p of Array.from(s.peers.values())) {
      if (!p || p.role !== 'sender' || !p.pc) continue;
      const cs = String(p.pc.connectionState || '');
      const is = String(p.pc.iceConnectionState || '');
      if (cs === 'connected' || is === 'connected' || is === 'completed') {
        count += 1;
      }
    }
  } catch {}
  return count;
}

function setFallbackDebug(state, patch) {
  const s = state || null;
  if (!s) return;
  if (!s.fallbackDebug || typeof s.fallbackDebug !== 'object') {
    s.fallbackDebug = {
      lastCheckAt: 0,
      lastReason: '',
      lastTriggered: false,
      lastPeerCount: 0,
      lastConnectedPeerCount: 0,
      lastWantedPeers: 0,
    };
  }
  Object.assign(s.fallbackDebug, patch || {});
}

async function switchToLiveKitFallback(room, reason) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return { ok: false, reason: 'no_room' };
  if (!s.enabled) return { ok: false, reason: 'not_enabled' };
  if (s.transport === 'livekit') return { ok: true, already: true };

  clearFallbackTimer(s);

  const track = s.localTrack || null;
  const audioTrack = s.localAudioTrack || null;
  if (!track) {
    setFallbackDebug(s, {
      lastCheckAt: Date.now(),
      lastReason: 'switch_failed_no_track',
      lastTriggered: false,
      lastPeerCount: countSenderPeers(s),
      lastConnectedPeerCount: countConnectedSenderPeers(s),
      lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
    });
    return { ok: false, reason: 'no_track' };
  }

  setFallbackDebug(s, {
    lastCheckAt: Date.now(),
    lastReason: String(reason || 'unknown'),
    lastTriggered: true,
    lastPeerCount: countSenderPeers(s),
    lastConnectedPeerCount: countConnectedSenderPeers(s),
    lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
  });

  closeAllSendingPeers(r, s);

  try {
    notifyUpdated(r);
  } catch {}

  const prof = getProfiles(s.quality, s.fps);
  const pubRes = await publishExistingScreenTrackToLiveKit(r, track, audioTrack, {
    maxBitrate: prof && prof.sender && prof.sender.maxBitrate,
    maxFramerate: normalizeFps(s.fps),
  });
  if (!pubRes || !pubRes.ok) {
    s.err = String((pubRes && pubRes.reason) || reason || 'livekit_publish_failed');
    setFallbackDebug(s, {
      lastCheckAt: Date.now(),
      lastReason: `livekit_publish_failed:${s.err}`,
      lastTriggered: true,
      lastPeerCount: countSenderPeers(s),
      lastConnectedPeerCount: countConnectedSenderPeers(s),
      lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
    });
    return { ok: false, reason: s.err };
  }

  s.transport = 'livekit';
  s.livekitPub = pubRes.pub || null;
  s.sending = false;

  try {
    notifyUpdated(r);
  } catch {}

  return { ok: true, reason: String(reason || '') };
}

// Ends our outgoing direct connections (telling each viewer) and leaves incoming streams alone.
function closeAllSendingPeers(room, state) {
  const r = room || null;
  const s = state || getState(r);
  if (!r || !s) return;
  try {
    const viewers = Array.from(s.peers.values())
      .filter((p) => p && p.role === 'sender')
      .map((p) => p.identity);
    for (const id of viewers) {
      try {
        publishSignal(r, { type: 'stop', dir: 's2r' }, id);
      } catch {}
      closePeer(s, id, 'sender');
    }
  } catch {}
}

function scheduleFallbackCheck(room) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return;

  clearFallbackTimer(s);

  if (!s.fallback || !s.fallback.enabled) {
    setFallbackDebug(s, {
      lastCheckAt: Date.now(),
      lastReason: 'fallback_disabled',
      lastTriggered: false,
      lastPeerCount: countSenderPeers(s),
      lastConnectedPeerCount: countConnectedSenderPeers(s),
      lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
    });
    return;
  }

  if (s.transport !== 'p2p') {
    setFallbackDebug(s, {
      lastCheckAt: Date.now(),
      lastReason: 'transport_not_p2p',
      lastTriggered: false,
      lastPeerCount: countSenderPeers(s),
      lastConnectedPeerCount: countConnectedSenderPeers(s),
      lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
    });
    return;
  }

  const timeoutMs = Math.max(500, Number(s.fallback.timeoutMs) || 0);
  if (timeoutMs <= 0) {
    setFallbackDebug(s, {
      lastCheckAt: Date.now(),
      lastReason: 'timeout_disabled',
      lastTriggered: false,
      lastPeerCount: countSenderPeers(s),
      lastConnectedPeerCount: countConnectedSenderPeers(s),
      lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
    });
    return;
  }

  setFallbackDebug(s, {
    lastCheckAt: Date.now(),
    lastReason: `timer_started_${timeoutMs}ms`,
    lastTriggered: false,
    lastPeerCount: countSenderPeers(s),
    lastConnectedPeerCount: countConnectedSenderPeers(s),
    lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
  });

  s.fallbackTimer = setTimeout(() => {
    try {
      const st = getState(r);
      if (!st || !st.enabled) {
        if (st) {
          setFallbackDebug(st, {
            lastCheckAt: Date.now(),
            lastReason: 'timer_fired_not_enabled',
            lastTriggered: false,
            lastPeerCount: countSenderPeers(st),
            lastConnectedPeerCount: countConnectedSenderPeers(st),
            lastWantedPeers: Math.max(0, Number(st.maxPeers) || 0),
          });
        }
        return;
      }

      if (st.transport !== 'p2p') {
        setFallbackDebug(st, {
          lastCheckAt: Date.now(),
          lastReason: 'timer_fired_transport_not_p2p',
          lastTriggered: false,
          lastPeerCount: countSenderPeers(st),
          lastConnectedPeerCount: countConnectedSenderPeers(st),
          lastWantedPeers: Math.max(0, Number(st.maxPeers) || 0),
        });
        return;
      }

      const wantsPeers = Math.max(0, Number(st.maxPeers) || 0);
      const peerCount = countSenderPeers(st);
      const connectedPeerCount = countConnectedSenderPeers(st);

      setFallbackDebug(st, {
        lastCheckAt: Date.now(),
        lastReason: 'timer_fired_checking',
        lastTriggered: false,
        lastPeerCount: peerCount,
        lastConnectedPeerCount: connectedPeerCount,
        lastWantedPeers: wantsPeers,
      });

      if (wantsPeers <= 0) {
        setFallbackDebug(st, {
          lastCheckAt: Date.now(),
          lastReason: 'no_wanted_peers',
          lastTriggered: false,
          lastPeerCount: peerCount,
          lastConnectedPeerCount: connectedPeerCount,
          lastWantedPeers: wantsPeers,
        });
        return;
      }

      if (peerCount === 0) {
        setFallbackDebug(st, {
          lastCheckAt: Date.now(),
          lastReason: 'p2p_no_peers',
          lastTriggered: true,
          lastPeerCount: peerCount,
          lastConnectedPeerCount: connectedPeerCount,
          lastWantedPeers: wantsPeers,
        });
        switchToLiveKitFallback(r, 'p2p_no_peers');
        return;
      }

      if (connectedPeerCount <= 0) {
        setFallbackDebug(st, {
          lastCheckAt: Date.now(),
          lastReason: 'p2p_timeout',
          lastTriggered: true,
          lastPeerCount: peerCount,
          lastConnectedPeerCount: connectedPeerCount,
          lastWantedPeers: wantsPeers,
        });
        switchToLiveKitFallback(r, 'p2p_timeout');
        return;
      }

      setFallbackDebug(st, {
        lastCheckAt: Date.now(),
        lastReason: 'p2p_ok_connected',
        lastTriggered: false,
        lastPeerCount: peerCount,
        lastConnectedPeerCount: connectedPeerCount,
        lastWantedPeers: wantsPeers,
      });
    } catch {}
  }, timeoutMs);
}

function notifyUpdated(room) {
  try {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('pa-p2p-screens-updated', { detail: { ok: true } }));
  } catch {}

  try {
    const r = room || null;
    if (!r) return;
    if (typeof r.__pa_p2p_rev !== 'number') r.__pa_p2p_rev = 0;
    r.__pa_p2p_rev += 1;
  } catch {}
}

function publishSignal(room, msg, targetIdentity) {
  const r = room || null;
  if (!r || !r.localParticipant) return;

  const out = {
    ...msg,
    __pa: 'p2p_screen_v1',
    to: targetIdentity || null,
    from: String(r.localParticipant.identity || r.localParticipant.sid || ''),
    ts: Date.now(),
  };

  const target = String(targetIdentity || '').trim();
  try {
    // Offers, answers and ICE candidates go only to the peer they are for.
    quietly(r.localParticipant.publishData(encode(out), target ? { reliable: true, destinationIdentities: [target] } : { reliable: true }));
  } catch {
    try {
      quietly(r.localParticipant.publishData(encode(out), { reliable: true }));
    } catch {}
  }
}

// publishData returns a promise: a rejection (data channel closing while leaving) must not surface as an
// unhandled error.
function quietly(p) {
  if (p && typeof p.catch === "function") p.catch(() => {});
}

function defaultStunServers() {
  return [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }];
}

function normalizeIceServersList(list) {
  const arr = Array.isArray(list) ? list : [];
  const out = [];
  for (const s of arr) {
    if (!s) continue;

    const urls = s.urls;
    const url = s.url;

    const u = urls || url;
    if (!u) continue;

    const entry = { urls: u };
    if (s.username != null) entry.username = s.username;
    if (s.credential != null) entry.credential = s.credential;
    if (s.credentialType != null) entry.credentialType = s.credentialType;

    out.push(entry);
  }
  return out;
}

async function fetchIceServersFromBackend() {
  try {
    const res = await fetch('/api/ice', { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    if (!data) return null;

    const servers = normalizeIceServersList(data.iceServers || data.ice_servers || data.servers || null);
    if (!servers || servers.length === 0) return null;

    return servers;
  } catch {
    return null;
  }
}

async function getIceServers(room) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return defaultStunServers();

  const now = Date.now();
  if (s.ice.cachedServers && now - (s.ice.cachedAt || 0) < (s.ice.cacheMs || 0)) {
    return s.ice.cachedServers;
  }

  let servers = null;

  try {
    if (typeof window !== 'undefined') {
      const w = window;
      const fromWindow =
        w.__PA_ICE_SERVERS ||
        w.__pa_ice_servers ||
        (w.__PA_CONFIG && (w.__PA_CONFIG.iceServers || w.__PA_CONFIG.ice_servers)) ||
        null;
      servers = normalizeIceServersList(fromWindow);
    }
  } catch {}

  if (!servers || servers.length === 0) {
    servers = await fetchIceServersFromBackend();
  }

  if (!servers || servers.length === 0) {
    servers = defaultStunServers();
  }

  s.ice.cachedServers = servers;
  s.ice.cachedAt = now;

  return servers;
}

function trackEnded(track, onEnded) {
  const t = track || null;
  if (!t) return;

  try {
    t.onended = () => {
      try {
        onEnded && onEnded();
      } catch {}
    };
  } catch {}

  try {
    if (typeof t.addEventListener === 'function') {
      t.addEventListener('ended', () => {
        try {
          onEnded && onEnded();
        } catch {}
      });
    }
  } catch {}
}

// Peers are keyed by direction and identity: while A shares to B and B shares to A at the same time
// they are two separate connections (a single identity key made them overwrite each other).
function peerKey(role, identity) {
  return `${role === 'sender' ? 'send' : 'recv'}:${String(identity || '').trim()}`;
}

function getPeer(state, identity, role) {
  const id = String(identity || '').trim();
  if (!id) return null;
  if (role) return state.peers.get(peerKey(role, id)) || null;
  return state.peers.get(peerKey('receiver', id)) || state.peers.get(peerKey('sender', id)) || null;
}

function ensurePeerMeta(peer) {
  if (!peer) return null;
  if (!peer.meta) {
    peer.meta = {
      statsTimer: null,
      lastIsRelay: null,
      lastAppliedAt: 0,
      closing: false,
      wantRestartOnFailure: true,
      restarting: false,
      lastRestartAt: 0,
    };
  }
  return peer.meta;
}

async function readSelectedCandidatePairIsRelay(pc) {
  const p = pc || null;
  if (!p || typeof p.getStats !== 'function') return null;

  let stats = null;
  try {
    stats = await p.getStats(null);
  } catch {
    return null;
  }

  if (!stats) return null;

  const byId = new Map();
  try {
    stats.forEach((r) => {
      if (r && r.id) byId.set(r.id, r);
    });
  } catch {}

  let pair = null;

  try {
    for (const r of byId.values()) {
      if (!r) continue;
      if (r.type === 'transport' && r.selectedCandidatePairId) {
        const cp = byId.get(r.selectedCandidatePairId);
        if (cp && cp.type === 'candidate-pair') {
          pair = cp;
          break;
        }
      }
    }
  } catch {}

  if (!pair) {
    try {
      for (const r of byId.values()) {
        if (!r) continue;
        if (r.type !== 'candidate-pair') continue;
        if (r.selected === true) {
          pair = r;
          break;
        }
      }
    } catch {}
  }

  if (!pair) {
    try {
      let best = null;
      for (const r of byId.values()) {
        if (!r) continue;
        if (r.type !== 'candidate-pair') continue;
        if (r.state !== 'succeeded') continue;
        const nominated = r.nominated === true;
        if (!best) best = r;
        else if (nominated && best.nominated !== true) best = r;
      }
      if (best) pair = best;
    } catch {}
  }

  if (!pair) return null;

  const localId = pair.localCandidateId || pair.localCandidateId === 0 ? pair.localCandidateId : null;
  const remoteId = pair.remoteCandidateId || pair.remoteCandidateId === 0 ? pair.remoteCandidateId : null;

  const lc = localId ? byId.get(localId) : null;
  const rc = remoteId ? byId.get(remoteId) : null;

  const lcType = lc && lc.candidateType ? String(lc.candidateType) : '';
  const rcType = rc && rc.candidateType ? String(rc.candidateType) : '';

  if (lcType === 'relay' || rcType === 'relay') return true;

  return false;
}

function trySetSenderParams(
  sender,
  { maxBitrate, minBitrate, maxFramerate, degradationPreference, priority = '', networkPriority = '' } = {}
) {
  const s = sender || null;
  if (!s || typeof s.getParameters !== 'function' || typeof s.setParameters !== 'function') return;

  try {
    const p = s.getParameters() || {};
    if (!Array.isArray(p.encodings) || p.encodings.length === 0) p.encodings = [{}];

    if (typeof maxBitrate === 'number' && Number.isFinite(maxBitrate) && maxBitrate > 0) {
      p.encodings[0].maxBitrate = Math.floor(maxBitrate);
    }

    if (typeof minBitrate === 'number' && Number.isFinite(minBitrate) && minBitrate > 0) {
      p.encodings[0].minBitrate = Math.floor(minBitrate);
    }

    if (typeof maxFramerate === 'number' && Number.isFinite(maxFramerate) && maxFramerate > 0) {
      p.encodings[0].maxFramerate = Math.floor(maxFramerate);
    }

    if (degradationPreference) {
      p.degradationPreference = String(degradationPreference);
    }

    try {
      if (priority) p.encodings[0].priority = String(priority);
    } catch {}

    try {
      if (networkPriority) p.encodings[0].networkPriority = String(networkPriority);
    } catch {}

    s.setParameters(p).catch(() => {});
  } catch {}
}


function getSupportedMediaConstraints() {
  try {
    if (navigator.mediaDevices && typeof navigator.mediaDevices.getSupportedConstraints === 'function') {
      return navigator.mediaDevices.getSupportedConstraints() || {};
    }
  } catch {}
  return {};
}

async function tryTuneCapturedAudioTrack(audioTrack) {
  const track = audioTrack || null;
  if (!track) return { ok: false, reason: 'no_audio_track' };

  try {
    if ('contentHint' in track) track.contentHint = 'music';
  } catch {}

  if (typeof track.applyConstraints !== 'function') {
    return { ok: false, reason: 'no_applyConstraints' };
  }

  const supported = getSupportedMediaConstraints();
  const audio = {};

  if (supported.echoCancellation) audio.echoCancellation = false;
  if (supported.noiseSuppression) audio.noiseSuppression = false;
  if (supported.autoGainControl) audio.autoGainControl = false;
  if (supported.channelCount) audio.channelCount = 2;
  if (supported.sampleRate) audio.sampleRate = 48000;
  if (supported.sampleSize) audio.sampleSize = 16;
  if (supported.latency) audio.latency = 0.02;
  if (supported.suppressLocalAudioPlayback) audio.suppressLocalAudioPlayback = false;
  if (supported.restrictOwnAudio) audio.restrictOwnAudio = false;

  if (!Object.keys(audio).length) {
    return { ok: false, reason: 'no_supported_audio_constraints' };
  }

  try {
    await track.applyConstraints(audio);
    let settings = null;
    try {
      if (typeof track.getSettings === 'function') settings = track.getSettings();
    } catch {}
    return { ok: true, settings };
  } catch (e) {
    return { ok: false, reason: String((e && e.message) || e || 'apply_audio_constraints_failed') };
  }
}

function parseFmtpLineToMap(line) {
  const text = String(line || '').trim();
  const parts = text.split(/\s+/);
  const joined = parts.length > 1 ? parts.slice(1).join(' ') : '';
  const out = new Map();

  for (const part of joined.split(';')) {
    const item = String(part || '').trim();
    if (!item) continue;
    const eq = item.indexOf('=');
    if (eq === -1) {
      out.set(item, '');
      continue;
    }
    const key = String(item.slice(0, eq)).trim();
    const value = String(item.slice(eq + 1)).trim();
    if (!key) continue;
    out.set(key, value);
  }

  return out;
}

function buildFmtpLine(payloadType, map) {
  const params = [];
  for (const [key, value] of map.entries()) {
    if (!key) continue;
    if (value == null || value === '') params.push(String(key));
    else params.push(`${String(key)}=${String(value)}`);
  }
  return `a=fmtp:${payloadType} ${params.join(';')}`;
}

function mungeOpusSdp(sdp, { maxAverageBitrate = 192000 } = {}) {
  const input = String(sdp || '');
  if (!input) return input;

  const lines = input.split(/\r?\n/);
  const opusPayloads = [];

  for (const line of lines) {
    const match = /^a=rtpmap:(\d+)\s+opus\/48000(?:\/2)?$/i.exec(String(line || '').trim());
    if (match && match[1]) opusPayloads.push(String(match[1]));
  }

  if (!opusPayloads.length) return input;

  const patchPayload = (payloadType) => {
    let fmtpIndex = -1;
    for (let i = 0; i < lines.length; i += 1) {
      if (new RegExp(`^a=fmtp:${payloadType}\\s`, 'i').test(lines[i])) {
        fmtpIndex = i;
        break;
      }
    }

    const params = fmtpIndex >= 0 ? parseFmtpLineToMap(lines[fmtpIndex]) : new Map();
    params.set('stereo', '1');
    params.set('sprop-stereo', '1');
    params.set('maxaveragebitrate', String(Math.max(64000, Math.floor(Number(maxAverageBitrate) || 192000))));
    params.set('useinbandfec', '1');
    params.set('usedtx', '0');
    params.set('cbr', '0');

    const nextLine = buildFmtpLine(payloadType, params);

    if (fmtpIndex >= 0) lines[fmtpIndex] = nextLine;
    else {
      let insertAt = -1;
      for (let i = 0; i < lines.length; i += 1) {
        if (new RegExp(`^a=rtpmap:${payloadType}\\s`, 'i').test(lines[i])) {
          insertAt = i + 1;
          break;
        }
      }
      if (insertAt >= 0) lines.splice(insertAt, 0, nextLine);
    }
  };

  for (const payloadType of opusPayloads) patchPayload(payloadType);

  return lines.join('\r\n');
}

function setPeer(state, identity, pc, role) {
  const id = String(identity || '').trim();
  if (!id) return;
  const r = role === 'sender' ? 'sender' : 'receiver';
  state.peers.set(peerKey(r, id), { pc, role: r, identity: id, meta: null });
}

function stopPeerNetMonitor(peer) {
  const p = peer || null;
  if (!p) return;
  const m = ensurePeerMeta(p);
  if (!m) return;

  if (m.statsTimer) {
    try {
      clearInterval(m.statsTimer);
    } catch {}
    m.statsTimer = null;
  }
}

function pickRemoteIdentities(room) {
  const r = room || null;
  if (!r) return [];

  const out = [];
  try {
    r.remoteParticipants?.forEach?.((p) => {
      const id = String(p?.identity || '').trim();
      if (id) out.push(id);
    });
  } catch {}

  return out;
}

function normalizeQuality(q) {
  const v = String(q || 'thumb').trim();
  if (v === 'thumb') return 'thumb';
  if (v === 'expanded') return 'expanded';
  if (v === '720') return '720';
  if (v === '1080') return '1080';
  if (v === '1440') return '1440';
  if (v === '2160') return '2160';
  return 'thumb';
}

function normalizeFps(fps) {
  const n = Number(fps);
  if (n === 60) return 60;
  if (n === 30) return 30;
  return 20;
}

// Motion (games, video) needs frame rate and more bits; 20 fps shares are usually text/slides where
// sharpness matters more. Base profiles were tuned for resolution only.
export function tuneScreenProfileForFps(profile, fps) {
  const f = normalizeFps(fps);
  if (!profile || !profile.sender) return profile;
  const scale = f >= 60 ? 1.6 : f >= 30 ? 1.2 : 1;
  const sender = { ...profile.sender };
  if (typeof sender.maxBitrate === 'number') sender.maxBitrate = Math.round(sender.maxBitrate * scale);
  if (typeof sender.minBitrate === 'number') sender.minBitrate = Math.round(sender.minBitrate * scale);
  sender.degradationPreference = f >= 30 ? 'maintain-framerate' : 'maintain-resolution';
  return { ...profile, sender, contentHint: f >= 30 ? 'motion' : 'detail' };
}

function getProfiles(quality, fps) {
  return tuneScreenProfileForFps(getBaseProfiles(quality, fps), fps);
}

function getBaseProfiles(quality, fps) {
  const q = normalizeQuality(quality);
  const f = normalizeFps(fps);

  const audioSender = {
    minBitrate: 96000,
    maxBitrate: 192000,
    priority: 'high',
    networkPriority: 'high',
  };

  if (q === 'expanded') {
    return {
      capture: { w: 1280, h: 720, fps: f },
      sender: {
        minBitrate: 200000,
        maxBitrate: 1000000,
        maxFramerate: f,
        degradationPreference: 'maintain-resolution',
        priority: 'medium',
        networkPriority: 'medium',
      },
      audioSender,
    };
  }

  if (q === '720') {
    return {
      capture: { w: 1280, h: 720, fps: f },
      sender: {
        minBitrate: 300000,
        maxBitrate: 1600000,
        maxFramerate: f,
        degradationPreference: 'maintain-resolution',
        priority: 'medium',
        networkPriority: 'medium',
      },
      audioSender,
    };
  }

  if (q === '1080') {
    return {
      capture: { w: 1920, h: 1080, fps: f },
      sender: {
        minBitrate: 400000,
        maxBitrate: 2500000,
        maxFramerate: f,
        degradationPreference: 'maintain-resolution',
        priority: 'medium',
        networkPriority: 'medium',
      },
      audioSender,
    };
  }

  if (q === '1440') {
    return {
      capture: { w: 2560, h: 1440, fps: f },
      sender: {
        minBitrate: 500000,
        maxBitrate: 4000000,
        maxFramerate: f,
        degradationPreference: 'maintain-resolution',
        priority: 'medium',
        networkPriority: 'medium',
      },
      audioSender,
    };
  }

  if (q === '2160') {
    return {
      capture: { w: 3840, h: 2160, fps: f },
      sender: {
        minBitrate: 800000,
        maxBitrate: 6000000,
        maxFramerate: f,
        degradationPreference: 'maintain-resolution',
        priority: 'medium',
        networkPriority: 'medium',
      },
      audioSender,
    };
  }

  return {
    capture: { w: 640, h: 360, fps: f },
    sender: {
      minBitrate: 150000,
      maxBitrate: 700000,
      maxFramerate: f,
      degradationPreference: 'maintain-framerate',
    },
    audioSender,
  };
}

function applyNetworkAdaptiveSenderParams(room, peerId, isRelay) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return;

  const id = String(peerId || '').trim();
  if (!id) return;

  const peer = s.peers.get(peerKey('sender', id));
  if (!peer || !peer.pc || peer.role !== 'sender') return;

  let senders = [];
  try {
    senders = peer.pc.getSenders ? peer.pc.getSenders() : [];
  } catch {
    senders = [];
  }

  const senderV = Array.isArray(senders) ? senders.find((x) => x && x.track && x.track.kind === 'video') : null;
  const senderA = Array.isArray(senders) ? senders.find((x) => x && x.track && x.track.kind === 'audio') : null;

  const prof = getProfiles(s.quality, s.fps);

  if (!isRelay) {
    try {
      if (senderV) trySetSenderParams(senderV, prof.sender);
      if (senderA) trySetSenderParams(senderA, prof.audioSender);
    } catch {}
    return;
  }

  const base = prof.sender || {};
  const cappedFps = normalizeFps(s.fps) === 60 ? 30 : normalizeFps(s.fps);

  const relayParams = {
    ...base,
    maxBitrate: 800000,
    maxFramerate: cappedFps,
    priority: 'medium',
    networkPriority: 'medium',
  };

  try {
    if (senderV) trySetSenderParams(senderV, relayParams);
    if (senderA) trySetSenderParams(senderA, prof.audioSender);
  } catch {}
}

async function pollPeerNetAndApply(room, peerId) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return;

  const id = String(peerId || '').trim();
  if (!id) return;

  const peer = s.peers.get(peerKey('sender', id));
  if (!peer || !peer.pc) return;

  const m = ensurePeerMeta(peer);
  if (!m || m.closing) return;

  const now = Date.now();
  if (now - (m.lastAppliedAt || 0) < 350) return;

  let isRelay = null;
  try {
    isRelay = await readSelectedCandidatePairIsRelay(peer.pc);
  } catch {
    isRelay = null;
  }

  if (typeof isRelay !== 'boolean') return;

  if (m.lastIsRelay === isRelay) return;

  m.lastIsRelay = isRelay;
  m.lastAppliedAt = now;

  try {
    applyNetworkAdaptiveSenderParams(r, id, isRelay);
  } catch {}
}

async function restartPeerAsSenderWithTurn(room, identity) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return;

  const id = String(identity || '').trim();
  if (!id) return;

  const peer = s.peers.get(peerKey('sender', id));
  if (!peer || peer.role !== 'sender') return;

  const m = ensurePeerMeta(peer);
  if (!m || m.closing) return;

  const now = Date.now();
  if (m.restarting) return;
  if (now - (m.lastRestartAt || 0) < 4000) return;

  m.restarting = true;
  m.lastRestartAt = now;

  setFallbackDebug(s, {
    lastCheckAt: Date.now(),
    lastReason: `restart_sender_${id}`,
    lastTriggered: false,
    lastPeerCount: countSenderPeers(s),
    lastConnectedPeerCount: countConnectedSenderPeers(s),
    lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
  });

  try {
    closePeer(s, id, 'sender');
  } catch {}

  try {
    if (s.localTrack && s.localStream) {
      await connectSenderTo(r, id, s.localTrack);
      try {
        applySenderProfileToAll(r);
      } catch {}
      try {
        notifyUpdated(r);
      } catch {}
    }
  } catch {}

  m.restarting = false;

  try {
    scheduleFallbackCheck(r);
  } catch {}
}

function bindPeerNetMonitor(room, identity) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return;

  const id = String(identity || '').trim();
  if (!id) return;

  const peer = s.peers.get(peerKey('sender', id));
  if (!peer || !peer.pc) return;

  const m = ensurePeerMeta(peer);
  if (!m) return;

  stopPeerNetMonitor(peer);

  const kick = () => {
    try {
      pollPeerNetAndApply(r, id);
    } catch {}
  };

  try {
    const pc = peer.pc;

    pc.oniceconnectionstatechange = () => {
      try {
        kick();
      } catch {}

      try {
        const st = String(pc.iceConnectionState || '');
        if (st === 'failed' || st === 'disconnected') {
          setFallbackDebug(s, {
            lastCheckAt: Date.now(),
            lastReason: `ice_${st}_${id}`,
            lastTriggered: false,
            lastPeerCount: countSenderPeers(s),
            lastConnectedPeerCount: countConnectedSenderPeers(s),
            lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
          });
          if (peer.role === 'sender') {
            restartPeerAsSenderWithTurn(r, id);
          }
        }
      } catch {}
    };

    pc.onconnectionstatechange = () => {
      try {
        kick();
      } catch {}

      try {
        const st = String(pc.connectionState || '');
        if (st === 'failed' || st === 'disconnected') {
          setFallbackDebug(s, {
            lastCheckAt: Date.now(),
            lastReason: `conn_${st}_${id}`,
            lastTriggered: false,
            lastPeerCount: countSenderPeers(s),
            lastConnectedPeerCount: countConnectedSenderPeers(s),
            lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
          });
          if (peer.role === 'sender') {
            restartPeerAsSenderWithTurn(r, id);
          }
        }
      } catch {}
    };

    pc.onicegatheringstatechange = () => {
      try {
        kick();
      } catch {}
    };
  } catch {}

  try {
    m.statsTimer = setInterval(() => {
      try {
        pollPeerNetAndApply(r, id);
      } catch {}
    }, 1500);
  } catch {
    m.statsTimer = null;
  }

  try {
    kick();
  } catch {}
}

function closePeer(state, identity, role) {
  const id = String(identity || '').trim();
  if (!id) return;

  if (!role) {
    closePeer(state, id, 'sender');
    closePeer(state, id, 'receiver');
    return;
  }

  const key = peerKey(role, id);
  const p = state.peers.get(key);
  if (!p) return;

  try {
    const m = ensurePeerMeta(p);
    if (m) m.closing = true;
  } catch {}

  try {
    stopPeerNetMonitor(p);
  } catch {}

  try {
    if (p.pc) p.pc.onicecandidate = null;
  } catch {}

  try {
    if (p.pc) p.pc.ontrack = null;
  } catch {}

  try {
    if (p.pc) p.pc.oniceconnectionstatechange = null;
  } catch {}

  try {
    if (p.pc) p.pc.onconnectionstatechange = null;
  } catch {}

  try {
    if (p.pc) p.pc.onicegatheringstatechange = null;
  } catch {}

  try {
    if (p.pc) p.pc.close();
  } catch {}

  state.peers.delete(key);
}

function stopReceivingFrom(room, identity) {
  const r = room || null;
  if (!r) return;

  const s = getState(r);
  if (!s) return;

  const id = String(identity || '').trim();
  if (!id) return;

  try {
    const existing = s.remoteStreamsByIdentity.get(id);
    if (existing) {
      try {
        stopTracks(existing);
      } catch {}
    }
  } catch {}

  s.remoteStreamsByIdentity.delete(id);
  closePeer(s, id, 'receiver');

  try {
    r.__pa_p2p_screens = getP2PScreenRemoteStreams(r);
  } catch {}

  try {
    notifyUpdated(r);
  } catch {}
}


function mergeIncomingTracksIntoStream(existingStream, ev) {
  let out = existingStream || null;

  try {
    if (!out) out = new MediaStream();
  } catch {
    out = existingStream || null;
  }

  if (!out) return null;

  const replaceByKind = (track) => {
    const t = track || null;
    if (!t) return;

    try {
      const tracks = Array.from(out.getTracks?.() || []);
      for (const existing of tracks) {
        if (!existing) continue;

        const sameObject = existing === t;
        const sameId = String(existing.id || '').trim() && String(existing.id || '').trim() === String(t.id || '').trim();
        const sameKind = String(existing.kind || '').trim() === String(t.kind || '').trim();

        if (sameObject || sameId) return;

        const existingEnded = String(existing.readyState || '') === 'ended';
        if (sameKind || existingEnded) {
          try {
            out.removeTrack(existing);
          } catch {}
          try {
            existing.stop();
          } catch {}
        }
      }
    } catch {}

    try {
      out.addTrack(t);
    } catch {}
  };

  try {
    replaceByKind(ev && ev.track ? ev.track : null);
  } catch {}

  try {
    const incoming = ev && ev.streams && ev.streams[0] ? ev.streams[0] : null;
    const tracks = Array.from(incoming?.getTracks?.() || []);
    for (const t of tracks) replaceByKind(t);
  } catch {}

  return out;
}

async function acceptOffer(room, fromIdentity, msg) {
  const r = room || null;
  if (!r) return;

  ensureBound(r);

  const s = getState(r);
  if (!s) return;

  const from = String(fromIdentity || '').trim();
  if (!from) return;

  stopReceivingFrom(r, from);

  const iceServers = await getIceServers(r);
  const pc = new RTCPeerConnection({ iceServers });
  setPeer(s, from, pc, 'receiver');

  pc.onicecandidate = (ev) => {
    if (!ev || !ev.candidate) return;
    publishSignal(r, { type: 'ice', dir: 'r2s', candidate: ev.candidate }, from);
  };

  pc.ontrack = (ev) => {
    try {
      const existing = s.remoteStreamsByIdentity.get(from) || null;
      const stream = mergeIncomingTracksIntoStream(existing, ev);
      if (!stream) return;

      s.remoteStreamsByIdentity.set(from, stream);

      try {
        r.__pa_p2p_screens = getP2PScreenRemoteStreams(r);
      } catch {}

      try {
        notifyUpdated(r);
      } catch {}
    } catch {}
  };

  try {
    bindPeerNetMonitor(r, from);
  } catch {}

  try {
    await pc.setRemoteDescription(msg.sdp);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    publishSignal(r, { type: 'answer', sdp: pc.localDescription }, from);
  } catch {
    stopReceivingFrom(r, from);
  }
}

async function acceptAnswer(room, fromIdentity, msg) {
  const r = room || null;
  if (!r) return;

  ensureBound(r);

  const s = getState(r);
  if (!s) return;

  const from = String(fromIdentity || '').trim();
  if (!from) return;

  const peer = getPeer(s, from, 'sender');
  if (!peer || !peer.pc) return;

  try {
    await peer.pc.setRemoteDescription(msg.sdp);
  } catch {}

  try {
    bindPeerNetMonitor(r, from);
  } catch {}
}

async function acceptIce(room, fromIdentity, msg) {
  const r = room || null;
  if (!r) return;

  ensureBound(r);

  const s = getState(r);
  if (!s) return;

  const from = String(fromIdentity || '').trim();
  if (!from) return;

  // 's2r' = candidate from a sender for our receiving connection; 'r2s' = from a viewer for our sending one.
  const role = msg.dir === 's2r' ? 'receiver' : msg.dir === 'r2s' ? 'sender' : '';
  const peer = getPeer(s, from, role);
  if (!peer || !peer.pc) return;

  try {
    if (msg.candidate) await peer.pc.addIceCandidate(msg.candidate);
  } catch {}

  if (peer.role === 'sender') {
    try {
      bindPeerNetMonitor(r, from);
    } catch {}
  }
}

async function captureScreen(quality, fps) {
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
    throw new Error('screen_share_unsupported');
  }

  const prof = getProfiles(quality, fps);
  const want = prof.capture;

  const captured = await captureScreenShareMedia({ width: want.w, height: want.h, fps: want.fps });
  const stream = captured.stream;
  const track = captured.track || null;
  const audioTrack = captured.audioTrack || null;

  try {
    if (audioTrack) await tryTuneCapturedAudioTrack(audioTrack);
  } catch {}

  if (!track) {
    stopTracks(stream);
    throw new Error('screen_track_missing');
  }

  try {
    // 'motion' keeps frame rate for games/video; 'detail' keeps text sharp.
    track.contentHint = (prof && prof.contentHint) || 'detail';
  } catch {}

  return { stream, track, audioTrack };
}

async function tryApplyTrackConstraints(track, quality, fps) {
  const t = track || null;
  if (!t) return { ok: false, reason: 'no_track' };

  const prof = getProfiles(quality, fps);
  const want = prof.capture;

  try {
    if (typeof t.applyConstraints !== 'function') return { ok: false, reason: 'no_applyConstraints' };

    await t.applyConstraints({
      width: { ideal: want.w },
      height: { ideal: want.h },
      frameRate: { ideal: want.fps, max: want.fps },
    });

    let got = null;
    try {
      if (typeof t.getSettings === 'function') got = t.getSettings();
    } catch {}

    return { ok: true, settings: got };
  } catch (e) {
    return { ok: false, reason: String((e && e.message) || e || 'apply_failed') };
  }
}

function tryApplyAllNetworkAdaptation(room) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return;

  try {
    s.peers.forEach((p) => {
      if (!p || !p.pc || p.role !== 'sender') return;
      try {
        pollPeerNetAndApply(r, p.identity);
      } catch {}
    });
  } catch {}
}

function applySenderProfileToAll(room) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return;

  try {
    const prof = getProfiles(s.quality, s.fps);

    s.peers.forEach((p) => {
      if (!p || !p.pc || p.role !== 'sender') return;

      try {
        const senders = p.pc.getSenders ? p.pc.getSenders() : [];
        const senderV = Array.isArray(senders) ? senders.find((x) => x && x.track && x.track.kind === 'video') : null;
        const senderA = Array.isArray(senders) ? senders.find((x) => x && x.track && x.track.kind === 'audio') : null;

        if (senderV) trySetSenderParams(senderV, prof.sender);
        if (senderA) trySetSenderParams(senderA, prof.audioSender);
      } catch {}
    });
  } catch {}

  try {
    tryApplyAllNetworkAdaptation(r);
  } catch {}
}

async function reconnectAllSenders(room) {
  const r = room || null;
  if (!r) return;

  const s = getState(r);
  if (!s) return;
  if (!s.localTrack || !s.localStream) return;

  let senderIds = [];
  try {
    senderIds = Array.from(s.peers.values())
      .filter((p) => p && p.role === 'sender')
      .map((p) => String(p.identity || '').trim())
      .filter(Boolean);
  } catch {
    senderIds = [];
  }

  for (const id of senderIds) {
    try {
      closePeer(s, id, 'sender');
    } catch {}

    try {
      await connectSenderTo(r, id, s.localTrack);
    } catch {}
  }
}

async function connectSenderTo(room, targetIdentity, localTrack) {
  const r = room || null;
  if (!r) return;

  ensureBound(r);

  const s = getState(r);
  if (!s) return;

  const to = String(targetIdentity || '').trim();
  if (!to) return;

  closePeer(s, to, 'sender');

  const iceServers = await getIceServers(r);
  const pc = new RTCPeerConnection({ iceServers });
  setPeer(s, to, pc, 'sender');

  pc.onicecandidate = (ev) => {
    if (!ev || !ev.candidate) return;
    publishSignal(r, { type: 'ice', dir: 's2r', candidate: ev.candidate }, to);
  };

  let senderV = null;
  let senderA = null;

  try {
    senderV = pc.addTrack(localTrack, s.localStream);
  } catch {}

  try {
    if (s.localAudioTrack) senderA = pc.addTrack(s.localAudioTrack, s.localStream);
  } catch {}

  try {
    const prof = getProfiles(s.quality, s.fps);
    if (senderV) trySetSenderParams(senderV, prof.sender);
    if (senderA) trySetSenderParams(senderA, prof.audioSender);
  } catch {}

  try {
    bindPeerNetMonitor(r, to);
  } catch {}

  try {
    const offer = await pc.createOffer({ offerToReceiveVideo: false, offerToReceiveAudio: false });

    const mungedOffer = {
      type: offer.type,
      sdp: mungeOpusSdp(offer.sdp || '', { maxAverageBitrate: 192000 }),
    };

    await pc.setLocalDescription(mungedOffer);
    publishSignal(r, { type: 'offer', sdp: pc.localDescription }, to);
  } catch {
    closePeer(s, to, 'sender');
  }
}

export function getP2PScreenErr(room) {
  const s = getState(room);
  return s ? String(s.err || '') : '';
}

export function getP2PScreenTransport(room) {
  const s = getState(room);
  return s ? String(s.transport || 'p2p') : 'p2p';
}

export function getP2PScreenEnabled(room) {
  const s = getState(room);
  return !!(s && s.enabled);
}

export function getP2PScreenFps(room) {
  const s = getState(room);
  return s ? normalizeFps(s.fps) : 20;
}

export function getP2PScreenQuality(room) {
  const s = getState(room);
  const q = s ? normalizeQuality(s.quality) : '720';
  return q === '1080' ? '1080' : '720';
}

export function requestP2PScreenHandshake(room) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s || !r.localParticipant) return { ok: false, reason: 'no_room' };
  if (!s.sending && !s.receiveEnabled) return { ok: false, reason: 'not_enabled' };

  try {
    publishSignal(r, { type: 'hello' }, null);
  } catch {}

  return { ok: true };
}

export function setP2PScreenReceiveEnabled(room, enabled) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s || !r.localParticipant) return { ok: false, reason: 'no_room' };

  const next = !!enabled;
  s.receiveEnabled = next;

  if (next) {
    try {
      publishSignal(r, { type: 'hello' }, null);
    } catch {}
    return { ok: true, enabled: true };
  }

  try {
    const ids = Array.from(s.remoteStreamsByIdentity.keys());
    for (const id of ids) {
      try {
        publishSignal(r, { type: 'stop', dir: 'r2s' }, id);
      } catch {}
      try {
        stopReceivingFrom(r, id);
      } catch {}
    }
  } catch {}

  try {
    publishSignal(r, { type: 'stop', dir: 'r2s' }, null);
  } catch {}

  return { ok: true, enabled: false };
}

export async function setP2PScreenFps(room, fps) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return { ok: false, reason: 'no_state' };

  const next = normalizeFps(fps);
  if (normalizeFps(s.fps) === next) return { ok: true, already: true };

  s.fps = next;

  try {
    applySenderProfileToAll(r);
  } catch {}

  if (s.enabled && s.sending && s.localTrack) {
    const applied = await tryApplyTrackConstraints(s.localTrack, s.quality, s.fps);

    if (!applied || !applied.ok) {
      try {
        const maxPeers = s.maxPeers;
        await stopP2PScreenShare(r);
        const res = await startP2PScreenShare(r, { maxPeers });
        return { ok: true, restarted: true, apply: applied, start: res };
      } catch (e) {
        return {
          ok: false,
          reason: String((e && e.message) || e || 'restart_failed'),
          apply: applied,
        };
      }
    }

    try {
      await reconnectAllSenders(r);
    } catch {}
  }

  try {
    notifyUpdated(r);
  } catch {}

  return { ok: true };
}

export async function setP2PScreenQuality(room, quality) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s) return { ok: false, reason: 'no_state' };

  const next = normalizeQuality(quality);
  if (s.quality === next) return { ok: true, already: true };

  s.quality = next;

  try {
    applySenderProfileToAll(r);
  } catch {}

  if (s.enabled && s.sending && s.localTrack) {
    const applied = await tryApplyTrackConstraints(s.localTrack, s.quality, s.fps);

    if (!applied || !applied.ok) {
      try {
        const maxPeers = s.maxPeers;
        await stopP2PScreenShare(r);
        const res = await startP2PScreenShare(r, { maxPeers });
        return { ok: true, restarted: true, apply: applied, start: res };
      } catch (e) {
        return {
          ok: false,
          reason: String((e && e.message) || e || 'restart_failed'),
          apply: applied,
        };
      }
    }

    try {
      await reconnectAllSenders(r);
    } catch {}
  }

  try {
    notifyUpdated(r);
  } catch {}

  return { ok: true };
}

export function getP2PScreenRemoteStreams(room) {
  const s = getState(room);
  const out = {};
  if (!s) return out;

  try {
    s.remoteStreamsByIdentity.forEach((stream, id) => {
      if (stream) out[String(id)] = stream;
    });
  } catch {}

  return out;
}

export function getP2PScreenDebugSnapshot(room) {
  const r = room || null;
  const s = getState(r);

  if (!r || !s) {
    return {
      ok: false,
      enabled: false,
      sending: false,
      transport: 'p2p',
      err: '',
      maxPeers: 0,
      peerKeys: [],
      remoteStreamKeys: [],
      quality: 'thumb',
      fps: 20,
      fallbackDebug: null,
    };
  }

  let local = null;
  try {
    if (s.localTrack && typeof s.localTrack.getSettings === 'function') {
      local = s.localTrack.getSettings();
    }
  } catch {}

  const peerMeta = [];
  try {
    s.peers.forEach((p, id) => {
      const m = p && p.meta ? p.meta : null;
      peerMeta.push({
        id,
        role: (p && p.role) || null,
        hasPc: !!(p && p.pc),
        connectionState: p && p.pc ? String(p.pc.connectionState || '') : '',
        iceConnectionState: p && p.pc ? String(p.pc.iceConnectionState || '') : '',
        isRelay: m && typeof m.lastIsRelay === 'boolean' ? m.lastIsRelay : null,
      });
    });
  } catch {}

  return {
    ok: true,
    enabled: !!s.enabled,
    sending: !!s.sending,
    transport: String(s.transport || 'p2p'),
    err: String(s.err || ''),
    maxPeers: Number(s.maxPeers) || 0,
    peerKeys: Array.from(s.peers.keys()),
    remoteStreamKeys: Array.from(s.remoteStreamsByIdentity.keys()),
    quality: String(s.quality || 'thumb'),
    fps: normalizeFps(s.fps),
    localSettings: local,
    peers: peerMeta,
    fallbackDebug: s.fallbackDebug ? { ...s.fallbackDebug } : null,
    iceServersCached: Array.isArray(s.ice.cachedServers) ? s.ice.cachedServers : null,
    iceCachedAt: Number(s.ice.cachedAt) || 0,
  };
}

function ensureDebugHook() {
  try {
    if (typeof window === 'undefined') return;

    if (!window.__pa_p2p_screen) window.__pa_p2p_screen = {};

    window.__pa_p2p_screen.snapshot = () => getP2PScreenDebugSnapshot(lastBoundRoom || window.__PA_LK_ROOM || null);

    window.__pa_p2p_screen.didFail = () => {
      const snap = getP2PScreenDebugSnapshot(lastBoundRoom || window.__PA_LK_ROOM || null);
      if (!snap || !snap.ok) return { ok: false, reason: 'no_snapshot' };
      const fd = snap.fallbackDebug || {};
      return {
        ok: true,
        transport: snap.transport,
        failed: !!fd.lastTriggered,
        reason: fd.lastReason || '',
        checkedAt: fd.lastCheckAt || 0,
        peerCount: fd.lastPeerCount || 0,
        connectedPeerCount: fd.lastConnectedPeerCount || 0,
        wantedPeers: fd.lastWantedPeers || 0,
      };
    };

    window.__pa_p2p_screen._debugPeers = () => {
      const r = lastBoundRoom || window.__PA_LK_ROOM || null;
      const s = r ? getState(r) : null;
      if (!s) return { ok: false, reason: 'no_state' };

      const peers = [];
      s.peers.forEach((p, id) => {
        peers.push({
          id,
          role: (p && p.role) || null,
          hasPc: !!(p && p.pc),
          connectionState: p && p.pc ? String(p.pc.connectionState || '') : '',
          iceConnectionState: p && p.pc ? String(p.pc.iceConnectionState || '') : '',
        });
      });

      return { ok: true, peers };
    };

    window.__pa_p2p_screen._getPc = (identity) => {
      const r = lastBoundRoom || window.__PA_LK_ROOM || null;
      const s = r ? getState(r) : null;
      if (!s) return null;
      const p = getPeer(s, identity);
      return p ? p.pc : null;
    };

    window.__pa_p2p_screen._pollRelayNow = async (identity) => {
      const r = lastBoundRoom || window.__PA_LK_ROOM || null;
      const s = r ? getState(r) : null;
      if (!r || !s) return { ok: false, reason: 'no_state' };
      const p = getPeer(s, identity);
      if (!p || !p.pc) return { ok: false, reason: 'no_peer' };
      let isRelay = null;
      try {
        isRelay = await readSelectedCandidatePairIsRelay(p.pc);
      } catch {
        isRelay = null;
      }
      return { ok: true, isRelay };
    };

    window.__pa_p2p_screen.bind = (room) => {
      try {
        initP2PScreenShare(room);
        return { ok: true };
      } catch (e) {
        return { ok: false, reason: String((e && e.message) || e || 'bind_failed') };
      }
    };

    window.__pa_p2p_screen.start = async (maxPeers = 1) => {
      try {
        const r = lastBoundRoom || window.__PA_LK_ROOM;
        if (!r) return { ok: false, reason: 'no_room' };
        return await startP2PScreenShare(r, { maxPeers });
      } catch (e) {
        return { ok: false, reason: String((e && e.message) || e || 'start_failed') };
      }
    };

    window.__pa_p2p_screen.stop = async () => {
      try {
        const r = lastBoundRoom || window.__PA_LK_ROOM;
        if (!r) return { ok: false, reason: 'no_room' };
        return await stopP2PScreenShare(r);
      } catch (e) {
        return { ok: false, reason: String((e && e.message) || e || 'stop_failed') };
      }
    };

    // Same switch-over a broadcast performs: moves the current share onto the LiveKit SFU.
    window.__pa_p2p_screen.toLiveKit = async () => {
      try {
        const r = lastBoundRoom || window.__PA_LK_ROOM;
        if (!r) return { ok: false, reason: 'no_room' };
        return await ensureScreenShareOnLiveKit(r);
      } catch (e) {
        return { ok: false, reason: String((e && e.message) || e || 'switch_failed') };
      }
    };

    window.__pa_p2p_screen.hello = () => {
      try {
        const r = lastBoundRoom || window.__PA_LK_ROOM;
        if (!r) return { ok: false, reason: 'no_room' };
        return requestP2PScreenHandshake(r);
      } catch (e) {
        return { ok: false, reason: String((e && e.message) || e || 'hello_failed') };
      }
    };

    window.__pa_p2p_screen._refreshIce = async () => {
      const r = lastBoundRoom || window.__PA_LK_ROOM;
      const s = r ? getState(r) : null;
      if (!s) return { ok: false, reason: 'no_state' };
      s.ice.cachedServers = null;
      s.ice.cachedAt = 0;
      const servers = await getIceServers(r);
      return { ok: true, servers };
    };
  } catch {}
}

function ensureBound(room) {
  const r = room || null;
  if (!r) return;

  lastBoundRoom = r;
  ensureDebugHook();

  const s = getState(r);
  if (!s || s.bound) return;

  s.bound = true;

  const onData = async (payload, participant) => {
    const msg = decode(payload);
    if (!msg || msg.__pa !== 'p2p_screen_v1') return;

    const to = msg.to;
    const me = String(r.localParticipant?.identity || r.localParticipant?.sid || '');
    if (to && String(to) !== me) return;

    // Trust LiveKit's identity for the sender; a mismatching self-reported "from" is a spoof.
    const verified = participant && participant.identity ? String(participant.identity) : '';
    if (verified && msg.from && String(msg.from) !== verified) return;
    const from = verified || String(msg.from || '');
    if (!from) return;

    try {
      const stMode = getState(r);
      if (stMode && stMode.transport === 'livekit') {
        if (msg.type === 'hello' || msg.type === 'offer' || msg.type === 'answer' || msg.type === 'ice') {
          try {
            publishSignal(r, { type: 'stop', dir: 's2r' }, from);
          } catch {}
        }
        return;
      }
    } catch {}

    if (msg.type === 'stream_available') {
      try {
        const st = getState(r);
        if (!st || st.sending || !st.receiveEnabled) return;
        publishSignal(r, { type: 'hello' }, from);
      } catch {}
      return;
    }

    if (msg.type === 'hello') {
      try {
        const st = getState(r);
        if (!st || !st.sending || !st.localTrack) return;

        const limit = Math.max(0, Number(st.maxPeers) || 0);
        const connected = Array.from(st.peers.values()).filter((p) => p && p.role === 'sender').length;
        const existing = st.peers.get(peerKey('sender', from));
        if (limit > 0 && connected >= limit && !(existing && existing.pc)) {
          // More viewers than direct connections allow: move the share to the LiveKit SFU so every
          // viewer gets it (previously extra viewers were silently ignored).
          switchToLiveKitFallback(r, 'viewer_limit');
          return;
        }

        if (existing && existing.role === 'sender' && existing.pc) return;

        await connectSenderTo(r, from, st.localTrack);
        try {
          applySenderProfileToAll(r);
        } catch {}
        try {
          notifyUpdated(r);
        } catch {}
      } catch {}
      return;
    }

    if (msg.type === 'offer') {
      try {
        const st = getState(r);
        if (!st || (!st.receiveEnabled && !st.sending)) {
          publishSignal(r, { type: 'stop', dir: 'r2s' }, from);
          return;
        }
        // The server cannot police direct media: refuse senders whose role / timeout forbids sharing.
        if (!remoteMayShare(r, from, 'screen')) {
          publishSignal(r, { type: 'stop', dir: 'r2s' }, from);
          return;
        }
      } catch {}
      await acceptOffer(r, from, msg);
      return;
    }

    if (msg.type === 'answer') {
      await acceptAnswer(r, from, msg);
      return;
    }

    if (msg.type === 'ice') {
      await acceptIce(r, from, msg);
      return;
    }

    if (msg.type === 'stop') {
      // 'r2s': a viewer no longer wants our stream. Otherwise the sharer stopped.
      if (msg.dir === 'r2s') closePeer(getState(r), from, 'sender');
      else stopReceivingFrom(r, from);
      return;
    }
  };

  try {
    r.on(RoomEvent.DataReceived, onData);
  } catch {}

  const onParticipantConnected = async () => {
    try {
      const st = getState(r);
      if (!st || !st.receiveEnabled || st.sending) return;
      publishSignal(r, { type: 'hello' }, null);
    } catch {}
  };

  const onParticipantDisconnected = (p) => {
    const id = String(p?.identity || '').trim();
    if (!id) return;

    try {
      stopReceivingFrom(r, id);
    } catch {}
    // Also drop our sending connection to them, or it keeps counting toward the viewer limit.
    try {
      closePeer(getState(r), id, 'sender');
    } catch {}
  };

  try {
    r.on(RoomEvent.ParticipantConnected, onParticipantConnected);
  } catch {}

  try {
    r.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
  } catch {}

  // Direct shares are not policed by the SFU: when someone loses Share screen mid-share, the sharer's
  // client stops and every receiver drops the stream.
  try {
    r.on(RoomEvent.ParticipantPermissionsChanged, (_prev, participant) => {
      if (!participant) return;
      if (participant === r.localParticipant) {
        const st = getState(r);
        if (st && st.enabled && !participantMayShare(participant.permissions, 'screen')) stopP2PScreenShare(r).catch(() => {});
        return;
      }
      if (!participantMayShare(participant.permissions, 'screen')) {
        try {
          stopReceivingFrom(r, participant.identity);
        } catch {}
      }
    });
  } catch {}
}

export function initP2PScreenShare(room) {
  const r = room || null;
  if (!r) return;

  ensureBound(r);

  try {
    r.__pa_p2p_screens = getP2PScreenRemoteStreams(r);
  } catch {}

  try {
    if (!r.__pa_p2p_local_screen_stream) r.__pa_p2p_local_screen_stream = null;
  } catch {}

  try {
    notifyUpdated(r);
  } catch {}
}

export async function startP2PScreenShare(room, { maxPeers = 1 } = {}) {
  const r = room || null;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };

  ensureBound(r);

  const s = getState(r);
  if (!s) return { ok: false, reason: 'no_state' };

  if (voiceState.permissions && voiceState.permissions.screen === false) {
    s.err = 'no_screen_permission';
    return { ok: false, reason: 'no_screen_permission' };
  }

  s.err = '';
  s.maxPeers = Math.max(0, Number(maxPeers) || 0);
  s.transport = 'p2p';
  s.livekitPub = null;

  setFallbackDebug(s, {
    lastCheckAt: Date.now(),
    lastReason: 'start_requested',
    lastTriggered: false,
    lastPeerCount: 0,
    lastConnectedPeerCount: 0,
    lastWantedPeers: s.maxPeers,
  });

  ensureDebugHook();

  try {
    await stopP2PScreenShare(r);
  } catch {}

  try {
    await getIceServers(r);
  } catch {}

  try {
    const q = normalizeQuality(s.quality);
    const { stream, track, audioTrack } = await captureScreen(q, s.fps);

    try {
      if (stream && audioTrack && typeof stream.getAudioTracks === 'function' && typeof stream.addTrack === 'function') {
        const hasAudio = Array.from(stream.getAudioTracks() || []).some((t) => {
          if (!t || !audioTrack) return false;
          if (t === audioTrack) return true;
          const tid = String(t.id || '').trim();
          const aid = String(audioTrack.id || '').trim();
          return !!tid && !!aid && tid === aid;
        });
        if (!hasAudio) stream.addTrack(audioTrack);
      }
    } catch {}

    s.localStream = stream;
    s.localTrack = track;
    s.localAudioTrack = audioTrack || null;
    s.enabled = true;

    s.sending = true;
    s.transport = 'p2p';
    s.livekitPub = null;

    try {
      r.__pa_p2p_local_screen_stream = stream;
    } catch {}

    trackEnded(track, () => {
      try {
        stopP2PScreenShare(r);
      } catch {}
    });


    const remotes = pickRemoteIdentities(r);
    const limited = remotes.slice(0, Math.max(0, Number(s.maxPeers) || 0));

    try {
      publishSignal(r, { type: 'stream_available' }, null);
    } catch {}

    setFallbackDebug(s, {
      lastCheckAt: Date.now(),
      lastReason: 'p2p_started',
      lastTriggered: false,
      lastPeerCount: countSenderPeers(s),
      lastConnectedPeerCount: countConnectedSenderPeers(s),
      lastWantedPeers: s.maxPeers,
    });

    try {
      scheduleFallbackCheck(r);
    } catch {}

    try {
      applySenderProfileToAll(r);
    } catch {}

    try {
      r.__pa_p2p_screens = getP2PScreenRemoteStreams(r);
    } catch {}

    try {
      notifyUpdated(r);
    } catch {}

    return { ok: true, peers: limited.length, hasAudio: !!s.localAudioTrack };
  } catch (e) {
    s.err = String((e && e.message) || e || 'p2p_screen_failed');

    try {
      stopTracks(s.localStream);
    } catch {}

    s.localStream = null;
    s.localTrack = null;
    s.localAudioTrack = null;
    s.enabled = false;
    s.sending = false;


    setFallbackDebug(s, {
      lastCheckAt: Date.now(),
      lastReason: `start_failed:${s.err}`,
      lastTriggered: false,
      lastPeerCount: countSenderPeers(s),
      lastConnectedPeerCount: countConnectedSenderPeers(s),
      lastWantedPeers: s.maxPeers,
    });

    try {
      r.__pa_p2p_local_screen_stream = null;
    } catch {}

    try {
      notifyUpdated(r);
    } catch {}

    return { ok: false, reason: s.err };
  }
}

// External broadcasts (LiveKit Egress) read the SFU copy of the share, so a direct share is moved there.
export async function ensureScreenShareOnLiveKit(room) {
  const r = room || null;
  const s = getState(r);
  if (!r || !s || !s.enabled || !s.localTrack) return { ok: false, reason: 'not_sharing' };
  if (s.transport === 'livekit') return { ok: true, already: true };
  return switchToLiveKitFallback(r, 'broadcast');
}

export async function stopP2PScreenShare(room) {
  const r = room || null;
  if (!r) return { ok: true };

  ensureBound(r);

  const s = getState(r);
  if (!s) return { ok: true };

  s.err = '';

  try {
    clearFallbackTimer(s);
  } catch {}

  try {
    if (s.transport === 'livekit') {
      if (s.livekitPub) {
        await unpublishScreenTrackFromLiveKit(r, s.livekitPub);
      } else if (s.localTrack) {
        await unpublishScreenTrackFromLiveKit(r, s.localTrack);
      }
    }
  } catch {}

  s.transport = 'p2p';
  s.livekitPub = null;

  // Stopping (or restarting) our own share must not end the streams we are watching.
  closeAllSendingPeers(r, s);

  try {
    stopTracks(s.localStream);
  } catch {}

  s.localStream = null;
  s.localTrack = null;
  s.localAudioTrack = null;

  try {
    r.__pa_p2p_screens = getP2PScreenRemoteStreams(r);
  } catch {}

  try {
    r.__pa_p2p_local_screen_stream = null;
  } catch {}


  setFallbackDebug(s, {
    lastCheckAt: Date.now(),
    lastReason: 'stopped',
    lastTriggered: false,
    lastPeerCount: 0,
    lastConnectedPeerCount: 0,
    lastWantedPeers: Math.max(0, Number(s.maxPeers) || 0),
  });

  // Mark stopped before telling listeners: a stop the UI did not start (permission revoked, the
  // browser's own "Stop sharing" bar) otherwise left the panel showing "Stop screen".
  s.enabled = false;
  s.sending = false;

  try {
    notifyUpdated(r);
  } catch {}

  ensureDebugHook();

  return { ok: true };
}

(function autoBindIfPossible() {
  try {
    ensureDebugHook();
    if (typeof window === 'undefined') return;

    const tryBind = () => {
      try {
        const r = window.__PA_LK_ROOM;
        if (r && r.state === 'connected') {
          initP2PScreenShare(r);
          return true;
        }
      } catch {}
      return false;
    };

    if (tryBind()) return;

    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (tryBind() || tries > 30) {
        try {
          clearInterval(iv);
        } catch {}
      }
    }, 500);
  } catch {}
})();